/* global gdAlertify */
import React, { useCallback, useEffect, useState } from "react";
import axiosInstance from "../../config/axiosConfig";
import EnviosLotes from "./EnviosLotes";
import EnviosPrueba from "./EnviosPrueba";
import EnviosCanales from "./EnviosCanales";
import EnviosParametros from "./EnviosParametros";
import { fechaHora, haceCuanto, mensajeError, minutosDesde } from "./enviosUi";

const TABS = [
    { id: "envios", label: "Envíos", icon: "bi-list-check" },
    { id: "probar", label: "Probar envío", icon: "bi-send-check" },
    { id: "canales", label: "Canales", icon: "bi-plug" },
    { id: "parametros", label: "Parámetros", icon: "bi-sliders" },
];

// El cron corre cada 5 min: si el último ciclo tiene más de 12 min, algo pasa.
const MINUTOS_ALERTA_CRON = 12;

const MotorEnvios = () => {
    const [tab, setTab] = useState("envios");
    const [resumen, setResumen] = useState(null);
    const [procesando, setProcesando] = useState(false);
    const [refrescoLotes, setRefrescoLotes] = useState(0);

    const cargarResumen = useCallback(async () => {
        try {
            const { data } = await axiosInstance.get("/envios/resumen");
            setResumen(data?.data || null);
        } catch (error) {
            console.error("Error al obtener el resumen de envíos", error);
            gdAlertify.notiError(mensajeError(error, "No se pudo cargar el estado del motor de envíos."));
        }
    }, []);

    useEffect(() => {
        cargarResumen();
        const intervalo = setInterval(cargarResumen, 60000);
        return () => clearInterval(intervalo);
    }, [cargarResumen]);

    const procesarAhora = async () => {
        setProcesando(true);
        gdAlertify.showLoader();
        try {
            const { data } = await axiosInstance.post("/envios/procesar");
            const r = data?.data || {};
            if (r.omitido) {
                gdAlertify.notiWarning(r.omitido);
            } else {
                const partes = Object.entries(r.canales || {}).map(([codigo, s]) =>
                    s.sin_cupo ? `${codigo}: sin cupo` : `${codigo}: ${s.enviados || 0} enviados${s.error_canal ? " (" + s.error_canal + ")" : ""}`
                );
                gdAlertify.notiSuccess(partes.length ? partes.join(" · ") : "Ciclo ejecutado: no había canales activos.");
            }
            cargarResumen();
            setRefrescoLotes((n) => n + 1);
        } catch (error) {
            gdAlertify.notiError(mensajeError(error, "No se pudo procesar la cola."));
        } finally {
            gdAlertify.hideLoader();
            setProcesando(false);
        }
    };

    const parametros = resumen?.parametros;
    const ultimo = resumen?.ultimo_ciclo;
    const minutosUltimo = minutosDesde(ultimo?.fecha);
    // Solo se avisa si hay cron instalado: procesando a mano no hay ciclos automáticos que esperar.
    const cronAtrasado = parametros?.cron_instalado && parametros?.activo && (minutosUltimo === null || minutosUltimo > MINUTOS_ALERTA_CRON);

    return (
        <div className="container-fluid mt-4 px-4">
            <div className="card shadow mb-3">
                <div className="card-header bg-dark text-white d-flex justify-content-between align-items-center">
                    <h4 className="mb-0">
                        <i className="bi bi-send me-2"></i>
                        Motor de Envíos
                    </h4>
                    <button className="btn btn-primary" onClick={procesarAhora} disabled={procesando}
                        title="Corre un ciclo ahora, sin esperar al cron (ignora horario y motor detenido)">
                        <i className="bi bi-play-circle me-2"></i>
                        Procesar cola ahora
                    </button>
                </div>

                <div className="card-body">
                    {parametros?.modo_demo && (
                        <div className="alert alert-warning py-2">
                            <i className="bi bi-cone-striped me-2"></i>
                            <strong>Modo demo:</strong> todo se envía a {parametros.demo_email || <em>(sin correo demo)</em>} /{" "}
                            {parametros.demo_telefono || <em>(sin WhatsApp demo)</em>}, no a los destinatarios reales.
                            Se cambia en <strong>Parámetros</strong>.
                        </div>
                    )}
                    <div className="row g-3">
                        {(resumen?.cuota || []).map((c) => {
                            const pct = c.limite > 0 ? Math.min(100, Math.round((c.usados / c.limite) * 100)) : 0;
                            const color = pct >= 90 ? "danger" : pct >= 70 ? "warning" : "success";
                            return (
                                <div className="col-md-4" key={c.canal_id}>
                                    <div className="border rounded p-3 h-100">
                                        <div className="d-flex justify-content-between align-items-center mb-2">
                                            <strong>
                                                <i className={`bi ${c.tipo === "whatsapp" ? "bi-whatsapp text-success" : "bi-envelope text-primary"} me-2`}></i>
                                                {c.nombre}
                                            </strong>
                                            <span className={`badge ${c.activo ? "bg-success" : "bg-secondary"}`}>
                                                {c.activo ? "Activo" : "Inactivo"}
                                            </span>
                                        </div>
                                        {c.limite > 0 ? (
                                            <>
                                                <div className="progress mb-1" style={{ height: 8 }}>
                                                    <div className={`progress-bar bg-${color}`} style={{ width: `${pct}%` }}></div>
                                                </div>
                                                <small className="text-muted">
                                                    {c.usados} de {c.limite} en 24 h ({c.cuenta}) · quedan <strong>{c.disponible}</strong>
                                                </small>
                                            </>
                                        ) : (
                                            <small className="text-muted">{c.usados} en 24 h · sin límite propio</small>
                                        )}
                                    </div>
                                </div>
                            );
                        })}

                        <div className="col-md-4">
                            <div className={`border rounded p-3 h-100 ${cronAtrasado ? "border-warning" : ""}`}>
                                <div className="d-flex justify-content-between align-items-center mb-2">
                                    <strong><i className="bi bi-clock-history me-2"></i>Motor</strong>
                                    {parametros && (
                                        <span className={`badge ${parametros.activo ? "bg-success" : "bg-danger"}`}>
                                            {parametros.activo ? "Encendido" : "Detenido"}
                                        </span>
                                    )}
                                </div>
                                {parametros && (
                                    <small className="d-block text-muted">
                                        Horario {parametros.horario_desde} a {parametros.horario_hasta} (hora de Chile)
                                    </small>
                                )}
                                <small className="d-block text-muted">
                                    Último ciclo: {ultimo ? `${fechaHora(ultimo.fecha)} (${haceCuanto(ultimo.fecha)})` : "nunca"}
                                    {ultimo?.resumen?.omitido && <> · <em>{ultimo.resumen.omitido}</em></>}
                                </small>
                                {cronAtrasado && (
                                    <small className="d-block text-warning-emphasis mt-1">
                                        <i className="bi bi-exclamation-triangle me-1"></i>
                                        El cron no ha corrido en los últimos {MINUTOS_ALERTA_CRON} min. Revisa el crontab del servidor.
                                    </small>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <ul className="nav nav-tabs">
                {TABS.map((t) => (
                    <li className="nav-item" key={t.id}>
                        <button className={`nav-link ${tab === t.id ? "active" : ""}`} onClick={() => setTab(t.id)}>
                            <i className={`bi ${t.icon} me-2`}></i>
                            {t.label}
                        </button>
                    </li>
                ))}
            </ul>

            <div className="card border-top-0 rounded-0 rounded-bottom shadow-sm mb-4">
                <div className="card-body">
                    {tab === "envios" && <EnviosLotes refresco={refrescoLotes} />}
                    {tab === "probar" && (
                        <EnviosPrueba parametros={parametros} onEnviado={() => { cargarResumen(); setRefrescoLotes((n) => n + 1); }} />
                    )}
                    {tab === "canales" && <EnviosCanales onGuardado={cargarResumen} />}
                    {tab === "parametros" && <EnviosParametros ultimoCiclo={ultimo} onGuardado={cargarResumen} />}
                </div>
            </div>
        </div>
    );
};

export default MotorEnvios;
